import {Component, EventEmitter, Inject, OnInit, Output} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef} from '@angular/material/dialog';
import {UserRestService} from '@core/modules/rest/user/user-rest.service';
import {UserSettingsModel} from '@core/modules/rest/user/response/user-settings.model';
import {UserDataModel} from '@core/modules/rest/user/response/user-data.model';
import {SettingsEnum} from '@core/enums/settings.enum';
import {SliderToggleResponseModel} from '@shared/components/slide-toggle/model/slider-toggle-response.model';
import {LanguageStyleEnum} from '@core/enums/language-style.enum';
import {PresentationModeEnum} from '@core/enums/presentation-mode.enum';
import {TranslateService} from '@ngx-translate/core';
import {getLanguageEnumByLanguageCode, LanguageEnum} from '@core/enums/language.enum';
import {ConsentUseCaseBasicModel} from '@core/modules/rest/consent-use-case/response/consent-use-case-basic.model';
import {PersonalSettingsModel} from '@shared/model/personal-settings.model';
import {AllUserSettingsRequestModel} from '@core/modules/rest/user/request/all-user-settings-request.model';
import {IndicationBasicWithChapterNumberModel} from '@core/modules/rest/indication/response/indication-basic-with-chapter-number.model';
import {filter, take} from 'rxjs/operators';
import * as fromActions from '../../../store/user-management-actions';
import {Store} from '@ngrx/store';
import {AppState} from '@core/reducers';
import {YesNoDialogComponent, YesNoDialogData} from '@shared/dialogs/yes-no-dialog/yes-no-dialog.component';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {ConsentRestService} from '@core/modules/rest/consent/consent-rest.service';
import {ChooseEducationPopupComponent} from '../choose-education-popup/choose-education-popup.component';
import {SaveEducationSettingsRequestModel} from '@core/modules/rest/user/request/save-education-settings-request.model';
import {NotificationService} from '@shared/components/notification/service/notification.service';

@UntilDestroy()
@Component({
    selector: 'app-permissions-popup',
    templateUrl: './permissions-popup.component.html',
    styleUrls: ['./permissions-popup.component.scss']
})
export class PermissionsPopupComponent implements OnInit {
    @Output() slideToggleEvent: EventEmitter<SettingsEnum> = new EventEmitter<SettingsEnum>();
    @Output() saveSettingsEvent: EventEmitter<void> = new EventEmitter<void>();

    user: UserDataModel;
    username: string;
    settings: UserSettingsModel | undefined;
    personalSettings: PersonalSettingsModel | undefined;
    loading = true;
    anamnesis = SettingsEnum.ANAMNESISQUESTIONNAIRE;
    commenting = SettingsEnum.COMMENTINGSINGLETYPE;
    basicMode = SettingsEnum.BASICMODE;
    saveDisabled = true;
    basicModeEnabled = false;
    language: LanguageEnum;
    indications: IndicationBasicWithChapterNumberModel[] = [];
    useCases: ConsentUseCaseBasicModel[] = [];
    indicationOptions = [];
    useCaseOptions = [];
    languageOptions = [];
    languageStyleOptions = [];
    presentationModeOptions = [];
    selectedLanguage: LanguageEnum = LanguageEnum.GERMAN;
    selectedLanguageStyle: LanguageStyleEnum = LanguageStyleEnum.STANDARD;
    selectedPresentationMode: PresentationModeEnum = PresentationModeEnum.TEXT;
    selectedIndication: string;
    selectedUseCase: string;
    organizationDefaultSettings: UserSettingsModel;
    basicIndicationId: string;
    hasEducation = false;
    previousBasicModel: boolean;
    previousBasicModeOption: any;

    constructor(@Inject(MAT_DIALOG_DATA) data: {
                    user: UserDataModel, indications: [], useCases: [],
                    organizationDefaultSettings: UserSettingsModel
                },
                private dialogRef: MatDialogRef<ChooseEducationPopupComponent>,
                private dialog: MatDialog,
                private userService: UserRestService, private translate: TranslateService,
                private store: Store<AppState>,
                private consentService: ConsentRestService,
                private notificationService: NotificationService) {
        this.user = data.user;
        this.indications = data.indications;
        this.useCases = data.useCases;
        this.organizationDefaultSettings = data.organizationDefaultSettings;
    }

    ngOnInit(): void {
        this.language = getLanguageEnumByLanguageCode(localStorage.getItem('languageCode'));
        const basicIndications = this.indications.filter(item => item.noOfAUnits !== 7);
        this.basicIndicationId = (basicIndications !== undefined && basicIndications.length > 0) ? basicIndications[0].id : this.indications[0].id;
        this.userService.user(this.user.userId).pipe(filter(value => !!value), take(1)).subscribe((data: UserDataModel) => {
            this.username = data.username;
            this.settings = JSON.parse(data.settings);
            this.personalSettings = JSON.parse(data.personalSettings);
            this.basicModeEnabled = this.settings.basicMode;
            this.loading = false;
            this.addSelectOptions();
            this.setAlreadySelectedValues();
            if (this.settings.basicMode) {
                this.settings.commentingSingleTypeOff = true;
                this.settings.lightLanguageStyleOff = false;
                this.settings.anamnesisQuestionnaireOff = true;
                this.settings.customUseCaseOff = true;
            }
        });
    }

    addSelectOptions(): void {
        this.languageOptions.push({
            selectId: LanguageEnum.ENGLISH_GB,
            selectValue: this.translate.instant('GENERAL.ENGLISH')
        });
        this.languageOptions.push({
            selectId: LanguageEnum.GERMAN,
            selectValue: this.translate.instant('GENERAL.GERMAN')
        });
        this.languageOptions.push({
            selectId: LanguageEnum.SERBIAN,
            selectValue: this.translate.instant('GENERAL.SERBIAN')
        });
        this.languageStyleOptions.push({
            selectId: LanguageStyleEnum.STANDARD,
            selectValue: this.translate.instant('ONBOARDING.LANGUAGE_STYLE.STANDARD_TITLE')
        });
        this.languageStyleOptions.push({
            selectId: LanguageStyleEnum.SIMPLE,
            selectValue: this.translate.instant('ONBOARDING.LANGUAGE_STYLE.SIMPLE_TITLE')
        });
        this.languageStyleOptions.push({
            selectId: LanguageStyleEnum.CUSTOM,
            selectValue: this.translate.instant('GENERAL.SELECTION_BY_PATIENT')
        });
        if (!this.settings.lightLanguageStyleOff) {
            this.addEasyOptionInArray();
        }
        this.presentationModeOptions.push({
            selectId: PresentationModeEnum.VOICE,
            selectValue: this.translate.instant('GENERAL.VOICE_OUTPUT')
        });
        this.presentationModeOptions.push({
            selectId: PresentationModeEnum.TEXT,
            selectValue: this.translate.instant('GENERAL.TEXT_OUTPUT')
        });
        this.presentationModeOptions.push({
            selectId: PresentationModeEnum.CUSTOM,
            selectValue: this.translate.instant('GENERAL.SELECTION_BY_PATIENT')
        });
        this.indications.forEach(indication => {
            if (!indication.indicationDisabled) {
                this.indicationOptions.push({
                    selectId: indication.id,
                    selectValue: indication.title
                });
            }
        });
        this.selectedIndication = this.basicIndicationId;
        this.useCases.forEach(useCase => {
            this.useCaseOptions.push({
                selectId: useCase.id,
                selectValue: useCase.title
            });
        });
        this.selectedUseCase = this.useCases[0]?.id;
    }

    async change(response: SliderToggleResponseModel): Promise<void> {
        const settingValue = !response.selected;
        switch (response.setting) {
            case SettingsEnum.LIGHTLANGUAGESTYLE:
                this.settings.lightLanguageStyleOff = settingValue;
                break;
            case SettingsEnum.CUSTOMUSECASE:
                this.settings.customUseCaseOff = settingValue;
                break;
            case SettingsEnum.ANAMNESISQUESTIONNAIRE:
                this.settings.anamnesisQuestionnaireOff = settingValue;
                break;
            case SettingsEnum.COMMENTINGSINGLETYPE:
                this.settings.commentingSingleTypeOff = settingValue;
                const doChange = await this.changeSingleCommentMode(response);
                if (!doChange) {
                    this.settings.commentingSingleTypeOff = !this.settings.commentingSingleTypeOff;
                }
                break;
            case SettingsEnum.BASICMODE:
                const educationDataResponse = await this.changeBasicMode(response);
                if (educationDataResponse !== undefined) {
                    this.setOtherSettingsAfterBasicModeChange(educationDataResponse);
                }
                break;
        }
        this.onSaveUsersSettings();
    }

    private getDefaultDialogConfig(): MatDialogConfig {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.autoFocus = false;
        dialogConfig.disableClose = false;
        return dialogConfig;
    }

    async openChangeEducationPopup(): Promise<boolean> {
        const data: YesNoDialogData = {
            icon: 'local_library',
            iconColor: 'warning',
            title: this.translate.instant('DIALOGS.CHANGE_EDUCATION.TITLE'),
            message: this.translate.instant('DIALOGS.CHANGE_EDUCATION.MESSAGE'),
            yesButtonText: this.translate.instant('GENERAL.CHANGE'),
            noButtonText: this.translate.instant('GENERAL.CANCEL')
        };

        const dialogConfig = this.getDefaultDialogConfig();
        dialogConfig.data = data;
        const dialogRef = this.dialog.open(YesNoDialogComponent, dialogConfig);

        dialogRef.componentInstance.noEvent.pipe(untilDestroyed(this)).subscribe(() => {
            dialogRef.close();
        });

        let confirmed = false;
        dialogRef.componentInstance.yesEvent.pipe(untilDestroyed(this)).subscribe(() => {
            dialogRef.close();
            confirmed = true;
        });
        return dialogRef.afterClosed().toPromise().then(() => {
            return Promise.resolve(confirmed);
        });
    }

    async confirmSingleCommentChange(noOfQuestions: number): Promise<boolean> {
        const dialogConfig = this.getDefaultDialogConfig();

        dialogConfig.data = {
            icon: 'local_library',
            iconColor: 'warning',
            title: this.translate.instant('DIALOGS.MERGE_QUESTIONS.TITLE'),
            message: this.translate.instant('DIALOGS.MERGE_QUESTIONS.MESSAGE', {numberOfQuestions: noOfQuestions}),
            yesButtonText: this.translate.instant('GENERAL.MERGE'),
            noButtonText: this.translate.instant('GENERAL.CANCEL')
        };

        const dialogRef = this.dialog.open(YesNoDialogComponent, dialogConfig);
        dialogRef.componentInstance.noEvent.pipe(untilDestroyed(this)).subscribe(() => {
            dialogRef.close();
        });

        let confirmed = false;
        dialogRef.componentInstance.yesEvent.pipe(untilDestroyed(this)).subscribe(_ => {
            confirmed = true;
            this.store.dispatch(fromActions.MergeUsersActiveConsentComments({userId: this.user.userId}));
            dialogRef.close();
        });

        return dialogRef.afterClosed().toPromise().then(_ => {
            return Promise.resolve(confirmed);
        });
    }

    async chooseEducationDialog(user: UserDataModel): Promise<SaveEducationSettingsRequestModel> {
        const personalSettings: PersonalSettingsModel = JSON.parse(user.personalSettings);
        const userSettings: UserSettingsModel = JSON.parse(user.settings);
        const dialogConfig = this.getDefaultDialogConfig();
        let availableIndications: IndicationBasicWithChapterNumberModel[];

        let selectedIndicationId;
        if (personalSettings && personalSettings.indicationId) {
            selectedIndicationId = personalSettings.indicationId;
            this.hasEducation = true;
        } else {
            this.hasEducation = false;
        }
        if (this.basicModeEnabled) {
            availableIndications = this.indications;
            this.saveDisabled = false;
        } else {
            availableIndications = this.indications.filter(item => !item.basicIndication);
            const chosenOne = availableIndications.filter(item => item.id === selectedIndicationId);
            this.saveDisabled = chosenOne.length === 0;
        }
        dialogConfig.data = {
            userId: user.userId,
            indications: availableIndications,
            useCases: this.useCases,
            selectedIndicationId,
            selectedUseCase: personalSettings?.useCaseId,
            allowCustomUseCase: !userSettings?.customUseCaseOff,
            hasEducation: this.hasEducation,
            saveDisabled: this.saveDisabled
        };

        const dialogRef = this.dialog.open(ChooseEducationPopupComponent, dialogConfig);
        dialogRef.componentInstance.closeDialogEvent.pipe(untilDestroyed(this)).subscribe(() => {
            dialogRef.close();
        });

        let confirmed = false;
        let educationData: SaveEducationSettingsRequestModel;
        dialogRef.componentInstance.saveEducationDataEvent.pipe(untilDestroyed(this)).subscribe(async (data: SaveEducationSettingsRequestModel) => {
            confirmed = true;
            educationData = data;
            this.store.dispatch(fromActions.SaveUserEducationSettings({
                request: data,
                executed: () => {
                    dialogRef.close();
                }
            }));
        });

        return dialogRef.afterClosed().toPromise().then(() => {
            return Promise.resolve(educationData);
        });
    }

    onClose(): void {
        this.dialogRef.close();
    }

    async changeBasicMode(response: SliderToggleResponseModel): Promise<SaveEducationSettingsRequestModel> {
        const settingValue = !response.selected;
        this.previousBasicModel = !this.settings.basicMode;
        this.previousBasicModeOption = !this.basicModeEnabled;
        this.settings.basicMode = !settingValue;
        this.basicModeEnabled = !settingValue;
        const educationDataResponse = await this.chooseEducationDialog(this.user);
        if (educationDataResponse === undefined) {
            this.settings.basicMode = !this.previousBasicModel;
            this.basicModeEnabled = !this.previousBasicModeOption;
        }
        return educationDataResponse;
    }

    async changeSingleCommentMode(response: SliderToggleResponseModel): Promise<boolean> {
        return new Promise((resolve) => {
            let canContinue = true;
            if (response.selected) {
                    this.store.dispatch(fromActions.GetStartedConsentWithQuestions({
                        userId: this.user.userId, executed: async (noOfQuestions) => {
                            if (noOfQuestions > 1) {
                                canContinue = await this.confirmSingleCommentChange(noOfQuestions);
                            }
                            resolve(canContinue);
                        }
                    }));
            } else {
                resolve(canContinue);
            }
        });
    }

    async onSelectLanguageStyleChange(selected: LanguageStyleEnum): Promise<void> {
        const previousSelectedLanguageStyle = this.selectedLanguageStyle;
        this.selectedLanguageStyle = selected;
        this.userService.getConsentingActive(this.user.userId).subscribe(async (consentingActive: boolean) => {
            const userEducationSettings: SaveEducationSettingsRequestModel = {
                userId: this.user.userId,
                indicationId: this.selectedIndication,
                useCaseId: this.selectedUseCase,
                customUseCaseAllowed: !this.settings.customUseCaseOff,
                deactivateStartedConsents: false
            };
            if (consentingActive) {
                const confirmed = await this.openChangeEducationPopup();
                if (!confirmed) {
                    this.selectedLanguageStyle = previousSelectedLanguageStyle;
                } else {
                    userEducationSettings.deactivateStartedConsents = true;
                    this.store.dispatch(fromActions.SaveUserEducationSettings({
                        request: userEducationSettings,
                        executed: () => {
                            this.notificationService.success(this.translate.instant('NOTIFICATION.EMAIL_INVITATION_SENT'));
                        }
                    }));
                    this.onSaveUsersSettings();
                }
            } else {
                this.store.dispatch(fromActions.SaveUserEducationSettings({
                    request: userEducationSettings,
                    executed: () => {
                    }
                }));
                this.onSaveUsersSettings();
            }
        });
    }

    onSelectPresentationModeChange(selected: PresentationModeEnum): void {
        this.selectedPresentationMode = selected;
        this.onSaveUsersSettings();
    }

    createPersonalSettingsString(): string {
        const settingsObject: PersonalSettingsModel = {
            language: this.selectedLanguage,
            languageStyle: this.selectedLanguageStyle,
            presentationMode: this.selectedPresentationMode,
            textSize: this.personalSettings.textSize,
            indicationId: this.selectedIndication,
            useCaseId: this.selectedUseCase
        };
        return JSON.stringify(settingsObject);
    }

    removeOptionFromArray(option: LanguageStyleEnum): void {
        this.languageStyleOptions = this.languageStyleOptions.filter(item => item.selectId !== option);
    }

    addEasyOptionInArray(): void {
        this.languageStyleOptions.splice(2, 0, {
            selectId: LanguageStyleEnum.EASY,
            selectValue: this.translate.instant('ONBOARDING.LANGUAGE_STYLE.EASY_TITLE')
        });
    }

    setOtherSettingsAfterBasicModeChange(educationData: SaveEducationSettingsRequestModel): void {
        this.selectedIndication = educationData.indicationId;
        this.selectedUseCase = educationData.useCaseId;
        if (this.basicModeEnabled) {
            this.selectedLanguage = this.personalSettings.language;
            this.selectedLanguageStyle = this.personalSettings.languageStyle ? this.personalSettings.languageStyle : LanguageStyleEnum.STANDARD;
            this.selectedPresentationMode = this.personalSettings.presentationMode ? this.personalSettings.presentationMode : PresentationModeEnum.TEXT;
            this.settings.commentingSingleTypeOff = true;
            this.settings.anamnesisQuestionnaireOff = true;
            this.settings.customUseCaseOff = true;
            this.settings.lightLanguageStyleOff = false;
        } else {
            this.settings.anamnesisQuestionnaireOff = this.organizationDefaultSettings.anamnesisQuestionnaireOff;
            this.settings.customUseCaseOff = this.organizationDefaultSettings.customUseCaseOff;
            this.settings.lightLanguageStyleOff = this.organizationDefaultSettings.lightLanguageStyleOff;
            this.settings.commentingSingleTypeOff = this.organizationDefaultSettings.commentingSingleTypeOff;
        }
    }

    setAlreadySelectedValues(): void {
        if (this.personalSettings) {
            this.selectedLanguage = this.personalSettings.language;
            this.selectedLanguageStyle = this.personalSettings.languageStyle;
            this.selectedPresentationMode = this.personalSettings.presentationMode;
            this.selectedIndication = this.personalSettings.indicationId ? this.personalSettings.indicationId : this.basicIndicationId;
            this.selectedUseCase = this.personalSettings.useCaseId ? this.personalSettings.useCaseId : this.useCases[0]?.id;
        }
    }

    onSaveUsersSettings(): void {
        const personalSettings = this.createPersonalSettingsString();
        const request: AllUserSettingsRequestModel = {
            userId: this.user.userId,
            settings: JSON.stringify(this.settings),
            personalSettings
        };
        this.userService.saveSettings(request).subscribe(() => {
            this.saveSettingsEvent.emit();
        });
        this.store.dispatch(fromActions.UpdateUserSettingsSuccess({
            userId: this.user.userId,
            settings: JSON.stringify(this.settings),
            personalSettings
        }));
    }

    toggleEasyLanguageOption(): boolean {
        this.settings.lightLanguageStyleOff = !this.settings?.lightLanguageStyleOff;

        this.userService.getConsentingActive(this.user.userId).subscribe(async (consentingActive: boolean) => {
            const userEducationSettings: SaveEducationSettingsRequestModel = {
                userId: this.user.userId,
                indicationId: this.selectedIndication,
                useCaseId: this.selectedUseCase,
                customUseCaseAllowed: !this.settings.customUseCaseOff,
                deactivateStartedConsents: false
            };
            if (consentingActive && this.selectedLanguageStyle === LanguageStyleEnum.EASY) {
                const confirmed = await this.openChangeEducationPopup();

                if (!confirmed) {
                    return this.settings.lightLanguageStyleOff = false;
                } else {
                    this.manageDropdownOptions();
                    userEducationSettings.deactivateStartedConsents = true;
                    this.store.dispatch(fromActions.SaveUserEducationSettings({
                        request: userEducationSettings,
                        executed: () => {
                            this.notificationService.success(this.translate.instant('NOTIFICATION.EMAIL_INVITATION_SENT'));
                        }
                    }));
                    this.onSaveUsersSettings();
                    return !this.settings.lightLanguageStyleOff;
                }
            } else {
                this.manageDropdownOptions();
                this.onSaveUsersSettings();
                return !this.settings.lightLanguageStyleOff;
            }
        });
        return this.settings.lightLanguageStyleOff;
    }

    private manageDropdownOptions(): void {
        if (this.settings.lightLanguageStyleOff === false) {
            this.addEasyOptionInArray();
        } else {
            this.removeOptionFromArray(LanguageStyleEnum.EASY);
            if (this.selectedLanguageStyle === LanguageStyleEnum.EASY) {
                this.selectedLanguageStyle = LanguageStyleEnum.STANDARD;
            }
        }
    }
}
